import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Clock, User, Share2, Linkedin, Twitter, Facebook } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useBlogContactPopup } from "@/hooks/useBlogContactPopup";
import DemoScheduler from "@/components/DemoScheduler";
import SEO from "@/components/SEO";

const BlogPostProdutividade = () => {
    const { isOpen, setIsOpen } = useBlogContactPopup(3000);

    return (
        <div className="min-h-screen bg-background">
            <SEO
                title="60% do Tempo Perdido em Tarefas Repetitivas: Como Resolver a Crise de Produtividade"
                description="60% do tempo das equipes é desperdiçado em tarefas repetitivas. Descubra como a Automação Inteligente pode transformar trabalho repetitivo em trabalho estratégico e recuperar a produtividade."
                canonical="https://aroan.com.br/blog/crise-produtividade"
                ogImage="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=1200&auto=format&fit=crop"
                author="Equipe Aroan"
                publishedTime="2024-11-25T00:00:00-03:00"
                keywords="produtividade, tarefas repetitivas, automação inteligente, eficiência operacional, redução de custos, trabalho estratégico"
            />
            <Header />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-gradient-hero overflow-hidden">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                            Produtividade
                        </Badge>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight animate-fade-in">
                            60% do Tempo Perdido em Tarefas Repetitivas: <span className="text-primary">Como Resolver a Crise de Produtividade</span>
                        </h1>

                        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in-delay-1">
                            <div className="flex items-center gap-2">
                                <User className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">Equipe Aroan</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">25 Nov, 2024</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">6 min de leitura</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Image */}
            <section className="pb-12">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl animate-scale-in">
                        <img
                            src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=1200&auto=format&fit=crop"
                            alt="Crise de Produtividade"
                            className="w-full h-[400px] sm:h-[500px] object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto">

                        {/* Introduction */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <p className="lead text-xl sm:text-2xl text-muted-foreground leading-relaxed font-medium">
                                A produtividade é o motor de qualquer empresa, mas a realidade em muitos departamentos de atendimento e operações é desanimadora. Seus colaboradores, os talentos que você investiu para contratar e treinar, estão gastando a maior parte do tempo em tarefas que não agregam valor.
                            </p>
                            <p>
                                Estudos de mercado e análises internas da Aroan indicam que <strong>60% do tempo das equipes de atendimento e gestão de serviços é consumido por tarefas repetitivas e manuais</strong> [1]. Esse tempo, gasto em <em>copy-paste</em>, alternância entre sistemas e preenchimento de formulários, é o verdadeiro custo oculto da ineficiência operacional.
                            </p>
                            <p>
                                A boa notícia é que a Automação Inteligente não apenas identifica esse problema, mas oferece uma solução que transforma o trabalho repetitivo em trabalho estratégico.
                            </p>
                        </div>

                        <hr className="my-12 border-border" />

                        {/* Section 1 */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <h2 className="text-3xl font-bold mb-6 text-foreground">Onde os 60% do Tempo Estão Sendo Perdidos?</h2>
                            <p>
                                O tempo é perdido principalmente na intersecção de dois problemas crônicos: a <strong>fragmentação de ferramentas</strong> e a <strong>automação rígida</strong>.
                            </p>

                            <div className="bg-card p-6 rounded-xl border border-border my-8">
                                <h3 className="text-xl font-bold mb-3 text-primary">1. A Alternância Constante de Contexto</h3>
                                <p className="mb-4">
                                    A maioria das empresas utiliza uma média de 3 a 5 sistemas diferentes para gerenciar um único processo de serviço [2]. Para resolver um ticket, o agente precisa:
                                </p>
                                <ol className="list-decimal pl-6 space-y-2 mb-4">
                                    <li>Verificar o e-mail ou chat (Sistema A).</li>
                                    <li>Abrir o CRM para ver o histórico do cliente (Sistema B).</li>
                                    <li>Abrir o sistema de gestão de processos para iniciar um workflow (Sistema C).</li>
                                    <li>Copiar e colar informações entre os três sistemas.</li>
                                    <li>Voltar ao Sistema A para responder.</li>
                                </ol>
                                <p className="mb-0">
                                    Essa alternância constante de contexto é o que chamamos de <strong>"trabalho repetitivo invisível"</strong>. Não é apenas o tempo gasto na tarefa, mas o custo cognitivo de ter que se reorientar a cada clique.
                                </p>
                            </div>

                            <div className="bg-card p-6 rounded-xl border border-border my-8">
                                <h3 className="text-xl font-bold mb-3 text-primary">2. A Automação que Quebra</h3>
                                <p className="mb-4">
                                    Muitas empresas já investiram em automação, mas a maioria dessas soluções é baseada em regras estáticas. Elas funcionam perfeitamente quando o processo segue o caminho ideal, mas falham miseravelmente quando há um desvio.
                                </p>
                                <ul className="list-disc pl-6 space-y-2 mb-4">
                                    <li><strong>Exemplo</strong>: Um chatbot que não consegue entender uma variação na pergunta do cliente.</li>
                                    <li><strong>Exemplo</strong>: Um workflow de aprovação que para porque um campo obrigatório não foi preenchido.</li>
                                </ul>
                                <p className="mb-0">
                                    Quando a automação quebra, o processo volta para o agente humano, que precisa gastar tempo para diagnosticar o erro, corrigir manualmente e reiniciar o processo. Esse tempo de correção é parte dos 60% perdidos.
                                </p>
                            </div>

                            <div className="bg-card p-6 rounded-xl border border-border my-8">
                                <h3 className="text-xl font-bold mb-3 text-primary">3. A Falta de Inteligência Contextual</h3>
                                <p className="mb-4">
                                    O tempo também é perdido porque a automação não é inteligente o suficiente para tomar decisões. Se um ticket de suporte é classificado como "urgente", a automação tradicional apenas o encaminha. A Automação Inteligente, por outro lado, faria:
                                </p>
                                <ol className="list-decimal pl-6 space-y-2 mb-4">
                                    <li>Classificar o ticket como urgente.</li>
                                    <li>Verificar o histórico do cliente no CRM (contexto).</li>
                                    <li>Identificar que o cliente é um <em>key account</em> (contexto).</li>
                                    <li>Notificar o gerente de contas via Slack.</li>
                                    <li>Abrir um ticket de alta prioridade no sistema de gestão.</li>
                                    <li>Enviar uma resposta personalizada ao cliente com o SLA de atendimento.</li>
                                </ol>
                                <p className="mb-0">
                                    A falta de contexto e de inteligência acionável faz com que o agente humano tenha que realizar todas essas etapas manualmente, consumindo o tempo que deveria ser dedicado a problemas mais complexos.
                                </p>
                            </div>
                        </div>

                        {/* Section 2 */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <h2 className="text-3xl font-bold mb-6 text-foreground">A Solução: Transformando o Tempo Repetitivo em Trabalho Estratégico</h2>
                            <p>
                                A chave para recuperar os 60% de tempo perdido é migrar da <strong>Automação Rígida</strong> para a <strong>Automação Inteligente</strong>.
                            </p>

                            <h3 className="text-2xl font-bold mt-8 mb-4 text-foreground">1. Unificação Real: O Fim da Alternância</h3>
                            <p>
                                A Aroan resolve a fragmentação ao unificar todos os canais de comunicação e todos os processos em um único <strong>Hub de Atendimento Inteligente</strong>.
                            </p>
                            <ul className="list-disc pl-6 space-y-2 mt-4">
                                <li><strong>O que muda</strong>: O agente não precisa mais alternar entre sistemas. O histórico do cliente, o status do processo e as ferramentas de automação estão em uma única tela.</li>
                                <li><strong>O Resultado</strong>: <strong>80% de redução no tempo de atendimento</strong> [2], pois o agente tem o contexto completo e as ferramentas necessárias para resolver o problema na primeira interação.</li>
                            </ul>

                            <h3 className="text-2xl font-bold mt-8 mb-4 text-foreground">2. A Inteligência Contextual da VIA</h3>
                            <p>
                                A <strong>VIA (Virtual Intelligent Assistant)</strong>, a inteligência artificial proprietária da Aroan, é o motor que transforma a automação. Ela não se limita a seguir regras; ela aprende com o contexto.
                            </p>
                            <ul className="list-disc pl-6 space-y-2 mt-4">
                                <li><strong>O que muda</strong>: A automação se torna flexível. A VIA entende a intenção, o contexto e o histórico, permitindo que os workflows se adaptem a desvios sem quebrar.</li>
                                <li><strong>O Resultado</strong>: A VIA pode automatizar a triagem, a classificação, o roteamento e até mesmo a resolução de 40-60% dos tickets, liberando o agente para o trabalho que exige empatia e julgamento.</li>
                            </ul>

                            <h3 className="text-2xl font-bold mt-8 mb-4 text-foreground">3. Foco no Trabalho Estratégico</h3>
                            <p>
                                Ao automatizar o trabalho repetitivo, a Automação Inteligente libera o tempo dos seus colaboradores para o que realmente importa:
                            </p>

                            <div className="overflow-x-auto my-8">
                                <table className="w-full border-collapse text-left">
                                    <thead>
                                        <tr className="border-b border-border">
                                            <th className="py-4 px-4 font-bold text-foreground">Antes (60% do Tempo)</th>
                                            <th className="py-4 px-4 font-bold text-primary">Depois (40% do Tempo)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="border-b border-border">
                                            <td className="py-4 px-4 text-muted-foreground">Copiar e colar dados</td>
                                            <td className="py-4 px-4 text-primary font-medium">Desenvolver novos produtos e serviços</td>
                                        </tr>
                                        <tr className="border-b border-border">
                                            <td className="py-4 px-4 text-muted-foreground">Alternar entre 3-5 sistemas</td>
                                            <td className="py-4 px-4 text-primary font-medium">Focar em estratégias de retenção de clientes</td>
                                        </tr>
                                        <tr className="border-b border-border">
                                            <td className="py-4 px-4 text-muted-foreground">Corrigir automações que quebraram</td>
                                            <td className="py-4 px-4 text-primary font-medium">Analisar dados para otimizar a operação</td>
                                        </tr>
                                        <tr className="border-b border-border">
                                            <td className="py-4 px-4 text-muted-foreground">Responder perguntas frequentes</td>
                                            <td className="py-4 px-4 text-primary font-medium">Construir relacionamentos de longo prazo com clientes</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <p>
                                A Automação Inteligente não é sobre demitir pessoas; é sobre <strong>liberar o potencial humano</strong>. É sobre transformar um custo operacional em um centro de inovação e satisfação do cliente.
                            </p>
                        </div>

                        {/* Section 3 */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <h2 className="text-3xl font-bold mb-6 text-foreground">O Caminho para a Recuperação de Produtividade</h2>
                            <p>
                                A crise de produtividade é real, mas a solução é acessível. Ao adotar uma plataforma AI-first, sua empresa pode reverter a perda de tempo e transformar a eficiência operacional.
                            </p>
                            <p>
                                Empresas que fizeram essa transição com a Aroan alcançaram:
                            </p>
                            <ul className="space-y-4 list-none pl-0 mt-6">
                                <li className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">✓</div>
                                    <span className="text-lg"><strong>80% de Redução</strong> no Tempo de Atendimento</span>
                                </li>
                                <li className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">✓</div>
                                    <span className="text-lg"><strong>60% de Redução</strong> nos Custos Operacionais</span>
                                </li>
                                <li className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">✓</div>
                                    <span className="text-lg"><strong>Payback do Investimento</strong> em 6 Meses</span>
                                </li>
                            </ul>
                            <p className="mt-8 font-medium text-xl text-primary">
                                Não permita que 60% do tempo de seus colaboradores continue sendo desperdiçado. Invista em inteligência que transforma o trabalho repetitivo em trabalho estratégico.
                            </p>
                        </div>

                        {/* References */}
                        <div className="text-sm text-muted-foreground border-t border-border pt-6 mb-12">
                            <p className="font-semibold mb-2">Referências:</p>
                            <p className="mb-1">[1] AROAN. <em>Análise interna de mercado e resultados de clientes</em>.</p>
                            <p>[2] AROAN. <em>Análise interna de mercado e resultados de clientes</em>.</p>
                        </div>

                        {/* Share */}
                        <div className="flex items-center justify-between border-y border-border py-6 mb-12">
                            <span className="font-semibold">Compartilhe este artigo:</span>
                            <div className="flex gap-4">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-600"
                                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Linkedin className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-400"
                                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent("60% do Tempo Perdido em Tarefas Repetitivas: Como Resolver a Crise de Produtividade")}`, '_blank')}
                                >
                                    <Twitter className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-700"
                                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Facebook className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={async () => {
                                        if (navigator.share) {
                                            try {
                                                await navigator.share({
                                                    title: '60% do Tempo Perdido em Tarefas Repetitivas',
                                                    text: 'Como Resolver a Crise de Produtividade',
                                                    url: window.location.href,
                                                });
                                            } catch (err) {
                                                console.error('Error sharing:', err);
                                            }
                                        } else {
                                            navigator.clipboard.writeText(window.location.href);
                                            alert("Link copiado para a área de transferência!");
                                        }
                                    }}
                                >
                                    <Share2 className="w-5 h-5" />
                                </Button>
                            </div>
                        </div>

                        {/* CTA */}
                        <div className="bg-foreground text-background rounded-2xl p-8 sm:p-12 text-center">
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                                Quer saber quanto tempo sua empresa está perdendo?
                            </h2>
                            <p className="text-background/80 mb-8 max-w-2xl mx-auto">
                                Descubra como a abordagem AI-First pode recuperar 60% do tempo da sua equipe.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button size="lg" variant="secondary" className="group" onClick={() => window.open('/materiais', '_self')}>
                                    Baixe o Guia Completo
                                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                                </Button>
                                <Button size="lg" variant="outline" className="bg-transparent border-background/20 text-background hover:bg-background/10" onClick={() => window.open('/#demo', '_self')}>
                                    Agende uma Demonstração
                                </Button>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <Footer />

            <DemoScheduler open={isOpen} onOpenChange={setIsOpen} />
        </div>
    );
};

export default BlogPostProdutividade;
